import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Check, X, Clock, ShieldCheck, Inbox, Eye } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-TZ").format(n || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";

const APPROVAL_ROLES = ["SuperAdmin", "Manager", "Director"];

export default function Approvals() {
  const { user } = useAuth();
  const canApprove = APPROVAL_ROLES.includes(user?.role);

  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [confirm, setConfirm] = useState(null); // { task, action }
  const [note, setNote] = useState("");
  const [viewing, setViewing] = useState(null); // { task, source, loading }

  const openView = async (task) => {
    setViewing({ task, source: null, loading: true });
    try {
      const { data } = await api.get(`/approvals/${task.id}/source`);
      setViewing({ task, source: data.source, loading: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setViewing({ task, source: null, loading: false });
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, s] = await Promise.all([
        api.get("/approvals/pending"),
        api.get("/approvals/all"),
        api.get("/approvals/stats"),
      ]);
      setPending(p.data.items || []);
      setHistory((a.data.items || []).filter((x) => !["pending_l1", "pending_l2"].includes(x.status)));
      setStats(s.data || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!confirm) return;
    const { task, action } = confirm;
    try {
      await api.post(`/approvals/${task.id}/${action}`, { note });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setConfirm(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!canApprove) {
    return (
      <div className="space-y-6" data-testid="approvals-page-locked">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">Approvals</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Approval Queue</h1>
        </div>
        <Card className="p-12 text-center rounded-xl border border-border bg-card">
          <ShieldCheck className="w-12 h-12 mx-auto text-muted-foreground" />
          <p className="font-display text-lg mt-4">Approval access required</p>
          <p className="text-sm text-muted-foreground mt-1">Only Manager, Director and SuperAdmin can review approvals.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">Approval Queue</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Pending Approvals</h1>
          <p className="text-sm text-muted-foreground mt-1">Review and action requests requiring your sign-off.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Awaiting You", value: stats.my_pending || 0, color: "amber", icon: Clock },
          { label: "Total Pending", value: stats.total_pending || 0, color: "blue", icon: Inbox },
          { label: "Approved", value: stats.approved || 0, color: "emerald", icon: Check },
          { label: "Rejected", value: stats.rejected || 0, color: "rose", icon: X },
        ].map((s) => {
          const Icon = s.icon;
          const colorMap = {
            amber: "text-amber-500 bg-amber-500/10 border-amber-500/20",
            blue: "text-blue-500 bg-blue-500/10 border-blue-500/20",
            emerald: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
            rose: "text-rose-500 bg-rose-500/10 border-rose-500/20",
          };
          return (
            <Card key={s.label} className="p-5 rounded-xl border border-border bg-card">
              <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${colorMap[s.color]}`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-4">{s.label}</p>
              <p className="font-display text-2xl font-semibold mt-1">{s.value}</p>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-3">
          {loading ? (
            [...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
          ) : pending.length === 0 ? (
            <Card className="p-12 text-center rounded-xl border border-border bg-card">
              <Check className="w-10 h-10 mx-auto text-emerald-500" />
              <p className="font-display text-lg mt-3">All clear</p>
              <p className="text-sm text-muted-foreground">No approvals waiting for you.</p>
            </Card>
          ) : pending.map((task) => (
            <Card key={task.id} className="kn-card p-5 rounded-xl border border-border bg-card" data-testid={`approval-${task.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">{task.type}</Badge>
                    <Badge variant="outline" className={`text-[10px] ${task.status === "pending_l1" ? "border-amber-500/40 text-amber-500" : "border-blue-500/40 text-blue-500"}`}>
                      Level {task.level} · {task.status === "pending_l1" ? "Manager review" : "SuperAdmin sign-off"}
                    </Badge>
                  </div>
                  <h3 className="font-display text-lg font-semibold mt-2">{task.title}</h3>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-xs text-muted-foreground">
                    <span>Requester: <strong className="text-foreground">{task.requester}</strong></span>
                    {task.amount > 0 && <span>Amount: <strong className="text-foreground">TZS {fmt(task.amount)}</strong></span>}
                    <span>Submitted: {fmtDate(task.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => openView(task)} data-testid={`view-${task.id}`}>
                    <Eye className="w-4 h-4 mr-2" /> View
                  </Button>
                  <Button variant="outline" onClick={() => setConfirm({ task, action: "reject" })} className="border-rose-500/40 text-rose-500 hover:bg-rose-500/10" data-testid={`reject-${task.id}`}>
                    <X className="w-4 h-4 mr-2" /> Reject
                  </Button>
                  <Button onClick={() => setConfirm({ task, action: "approve" })} className="kn-gradient-bg text-white border-0" data-testid={`approve-${task.id}`}>
                    <Check className="w-4 h-4 mr-2" /> Approve
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="history" className="space-y-3">
          {history.length === 0 ? (
            <Card className="p-12 text-center text-sm text-muted-foreground">No approval history yet.</Card>
          ) : history.map((task) => (
            <Card key={task.id} className="p-4 rounded-xl border border-border bg-card flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="text-[10px]">{task.type}</Badge>
              <p className="text-sm font-medium flex-1 truncate">{task.title}</p>
              <span className="text-xs text-muted-foreground">{task.requester}</span>
              <span className="text-xs text-muted-foreground">TZS {fmt(task.amount)}</span>
              <Badge className={`text-[10px] ${
                task.status === "approved" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" :
                task.status === "rejected" ? "bg-rose-500/10 text-rose-500 border-rose-500/20" :
                "bg-amber-500/10 text-amber-600 border-amber-500/20"
              } border`} variant="outline">
                {task.status.replace(/_/g, " ")}
              </Badge>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* View Source dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl" data-testid="approval-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              {viewing?.task?.type} · {viewing?.task?.title}
              {viewing?.task?.status && (
                <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-600">
                  {viewing.task.status.replace(/_/g, " ")}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>Full source record under review. Approve once you've verified the details.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {viewing?.loading ? (
              <Skeleton className="h-40" />
            ) : !viewing?.source ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                Source record could not be loaded (may have been deleted).
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(viewing.source)
                  .filter(([k]) => !["id", "_id", "created_by_id"].includes(k))
                  .map(([k, v]) => {
                    const isMoney = ["amount", "value", "price", "cost", "gross", "tax", "net", "salary", "unit_price", "credit_limit"].includes(k);
                    let display = v === null || v === undefined || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v);
                    if (typeof v === "number" && isMoney) display = `TZS ${new Intl.NumberFormat("en-TZ").format(v)}`;
                    return (
                      <div key={k} className="border border-border rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{k.replace(/_/g, " ")}</p>
                        <p className="text-sm font-medium mt-1 break-words">{display}</p>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setViewing(null)}>Close</Button>
            {viewing?.task && (
              <>
                <Button
                  variant="outline"
                  className="border-rose-500/40 text-rose-500 hover:bg-rose-500/10"
                  onClick={() => { const t = viewing.task; setViewing(null); setConfirm({ task: t, action: "reject" }); }}
                  data-testid="view-reject-button"
                >
                  <X className="w-4 h-4 mr-2" /> Reject
                </Button>
                <Button
                  className="kn-gradient-bg text-white border-0"
                  onClick={() => { const t = viewing.task; setViewing(null); setConfirm({ task: t, action: "approve" }); }}
                  data-testid="view-approve-button"
                >
                  <Check className="w-4 h-4 mr-2" /> Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              {confirm?.action === "approve" ? "Approve request?" : "Reject request?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.task?.type}: <strong>{confirm?.task?.title}</strong>
              {confirm?.action === "approve" && confirm?.task?.status === "pending_l1" && (
                <span className="block mt-2 text-amber-600">This will move it to SuperAdmin for final sign-off.</span>
              )}
              {confirm?.action === "approve" && confirm?.task?.status === "pending_l2" && (
                <span className="block mt-2 text-emerald-600">This is the final approval. The record will be marked approved.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Optional note…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="approval-note"
            className="mt-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setConfirm(null); setNote(""); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doAction}
              className={confirm?.action === "approve" ? "kn-gradient-bg text-white border-0" : "bg-destructive text-destructive-foreground"}
              data-testid="confirm-approval-button"
            >
              {confirm?.action === "approve" ? "Approve" : "Reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
